import { BrewManError } from "@/app/errors";
import {
  AssemblyVesselRequirement,
  AssemblyProcessGroup,
  AssemblyProcessLine,
} from "@/app/services/generated/assemblyService";
import { RecipeProcessLine } from "@/app/services/generated/recipeService";
import type {
  RecipeProcessGroup,
  RecipeVesselRequirement,
} from "@/app/services/generated/recipeService";
import Guid from "@/app/types/common/guid";
import { makeDeepRef } from "@/components/composables/reactivityTypes";
import messages, { translate, keys } from "@/plugins/i18n";
import type { Ref } from "vue";
import { watch } from "vue";

// Used to help with array type signatures
export type AssemblyOrRecipeProcessGroupArray = (
  | RecipeProcessGroup
  | AssemblyProcessGroup
)[];

export type AssemblyOrRecipeProcessLineArray = (
  | RecipeProcessLine
  | AssemblyProcessLine
)[];

export type AssemblyOrRecipeProcessRequirementArray = (
  | RecipeVesselRequirement
  | AssemblyVesselRequirement
)[];

export type AssemblyOrRecipeProcessGroup =
  | RecipeProcessGroup
  | AssemblyProcessGroup;

export type AssemblyOrRecipeProcessLine =
  | RecipeProcessLine
  | AssemblyProcessLine;

export type AssemblyOrRecipeVesselRequirement =
  | RecipeVesselRequirement
  | AssemblyVesselRequirement;

/**
 * Type safety within this composable is a little loosened around
 * (RecipeProcessGroup | AssemblyProcessGroup)[]
 *
 * Typescript won't allow us to use fns like `.find` over an array of RecipeProcessGroup[] | AssemblyProcessGroup[] as
 * (group: Recipe) => !== (group: Assembly), although when we are using them we know it will be safe.
 *
 * While there's a mixture of both used throughout (and the old process impl did similar), I've tried to use
 * the more strict typings where possible, and only cast at the point of usage
 */

export function buildProcessGroupGridKey(
  processGroup: AssemblyOrRecipeProcessGroup
) {
  return processGroup.id.toString();
}

export function buildProcessLineGridKey(
  processGroup: AssemblyOrRecipeProcessLine
) {
  return processGroup.id.toString();
}

export function getProductionGroupById(
  productionProcessGroups: AssemblyOrRecipeProcessGroupArray,
  productionProcessGroupId: Guid
): AssemblyOrRecipeProcessGroup {
  const productionProcessGroup = productionProcessGroups.find((group) =>
    group.id.equals(productionProcessGroupId)
  );

  if (!productionProcessGroup) {
    throw new BrewManError(
      translate(keys.recipe_process_group_unable_to_find, {
        id: productionProcessGroupId.toString(),
      }).toString()
    );
  }

  return productionProcessGroup;
}

export function isAssemblyProcessLine(
  processLine: AssemblyOrRecipeProcessLine
): processLine is AssemblyProcessLine {
  return processLine instanceof AssemblyProcessLine;
}

export function isRecipeProcessLine(
  processLine: AssemblyOrRecipeProcessLine
): processLine is RecipeProcessLine {
  return processLine instanceof RecipeProcessLine;
}

export function isAssemblyProcessGroup(
  processGroup: AssemblyOrRecipeProcessGroup
): processGroup is AssemblyProcessGroup {
  return processGroup instanceof AssemblyProcessGroup;
}

export function getProductionProcessGroupId(
  processLine: AssemblyOrRecipeProcessLine
): Guid {
  return isAssemblyProcessLine(processLine)
    ? processLine.readOnly.assemblyProcessGroupId
    : processLine.readOnly.recipeProcessGroupId;
}

export function convertRecipeProcessesGroupsToAssembly(
  recipeProcessGroups: RecipeProcessGroup[]
): AssemblyProcessGroup[] {
  return recipeProcessGroups.map((recipeGroup) => {
    const assemblyGroup = new AssemblyProcessGroup();
    assemblyGroup.id = Guid.create();
    assemblyGroup.name = recipeGroup.name;
    assemblyGroup.plannedDurationDays = recipeGroup.plannedDurationDays;

    assemblyGroup.lines = recipeGroup.lines.map((recipeLine) => {
      const assemblyLine = new AssemblyProcessLine();
      assemblyLine.id = Guid.create();
      assemblyLine.instruction = recipeLine.instruction;
      assemblyLine.instructionNotes = recipeLine.notes;
      assemblyLine.sequence = recipeLine.sequence;
      assemblyLine.isLocked = true; // Set locked from the beginning, as recipe processes are required

      return assemblyLine;
    });

    assemblyGroup.vesselRequirements = recipeGroup.vesselRequirements.map(
      (recipeRequirement) => {
        const assemblyRequirement = new AssemblyVesselRequirement();
        assemblyRequirement.id = Guid.create();
        assemblyRequirement.capacityLitres = recipeRequirement.capacityLitres;
        assemblyRequirement.vesselCapabilityId =
          recipeRequirement.vesselCapabilityId;

        return assemblyRequirement;
      }
    );

    return assemblyGroup;
  });
}

export function useRecipeProcesses(
  recipeProcessGroups: Ref<Readonly<AssemblyOrRecipeProcessGroupArray>>
) {
  const internalRecipeProcessGroups =
    makeDeepRef<AssemblyOrRecipeProcessGroupArray>([]);

  // Cases where we use edit dialogs to manipulate state, or if the groups just change naturally
  watch(
    recipeProcessGroups,
    () => {
      internalRecipeProcessGroups.value = recipeProcessGroups.value.map(
        (group) => group.clone()
      );
    },
    { immediate: true, deep: true }
  );

  function addOrUpdateRecipeProcessGroup(
    newOrUpdatedRecipeGroup: AssemblyOrRecipeProcessGroup
  ) {
    if (newOrUpdatedRecipeGroup.id.isEmpty()) {
      newOrUpdatedRecipeGroup.id = Guid.create();
    }

    const existingProcessGroupIndex =
      internalRecipeProcessGroups.value.findIndex((group) =>
        group.id.equals(newOrUpdatedRecipeGroup.id)
      );

    if (existingProcessGroupIndex === -1) {
      internalRecipeProcessGroups.value.push(newOrUpdatedRecipeGroup);
      return;
    }

    internalRecipeProcessGroups.value.splice(
      existingProcessGroupIndex,
      1,
      newOrUpdatedRecipeGroup
    );
  }

  function addOrUpdateRecipeProcessLineInGroup(
    newOrUpdatedProcessLine: AssemblyOrRecipeProcessLine,
    productionProcessGroupId?: Guid
  ) {
    const processGroupIdFromLine = getProductionProcessGroupId(
      newOrUpdatedProcessLine
    );

    const processGroupIdToSearch =
      productionProcessGroupId ?? processGroupIdFromLine;

    const recipeProcessGroup = getProductionGroupById(
      internalRecipeProcessGroups.value,
      processGroupIdToSearch
    );

    // If we're a new line, just add it
    if (newOrUpdatedProcessLine.id.isEmpty()) {
      newOrUpdatedProcessLine.id = Guid.create();
    }

    if (isAssemblyProcessLine(newOrUpdatedProcessLine)) {
      if (newOrUpdatedProcessLine.readOnly.assemblyProcessGroupId.isEmpty()) {
        newOrUpdatedProcessLine.readOnly.assemblyProcessGroupId =
          processGroupIdToSearch; // Setting it herelines so we can still use it in the grid for actions
      }
    } else {
      if (newOrUpdatedProcessLine.readOnly.recipeProcessGroupId.isEmpty()) {
        newOrUpdatedProcessLine.readOnly.recipeProcessGroupId =
          processGroupIdToSearch; // Setting it here so we can still use it in the grid for actions
      }
    }

    // Otherwise find our existing and update if found
    const existingProcessLineIndex = recipeProcessGroup.lines.findIndex(
      (line) => line.id.equals(newOrUpdatedProcessLine.id)
    );

    const looselyTypedProcessGroupLines =
      recipeProcessGroup.lines as AssemblyOrRecipeProcessLineArray;

    if (existingProcessLineIndex === -1) {
      const newSequence = Math.max(
        -1,
        ...recipeProcessGroup.lines.map((line) => line.sequence)
      );

      newOrUpdatedProcessLine.sequence = newSequence + 1;
      looselyTypedProcessGroupLines.push(newOrUpdatedProcessLine);
      return;
    }

    looselyTypedProcessGroupLines.splice(
      existingProcessLineIndex,
      1,
      newOrUpdatedProcessLine
    );
  }

  function removeRecipeProcessGroup(
    recipeProcessGroup: AssemblyOrRecipeProcessGroup
  ) {
    const recipeProcessGroupIndex = internalRecipeProcessGroups.value.findIndex(
      (group) => group.id.equals(recipeProcessGroup.id)
    );

    if (recipeProcessGroupIndex === -1) {
      throw new BrewManError(
        translate(keys.recipe_process_group_unable_to_find, {
          id: recipeProcessGroup.id.toString(),
        }).toString()
      );
    }

    internalRecipeProcessGroups.value.splice(recipeProcessGroupIndex, 1);
  }

  function reOrderRecipeProcessGroupLines(
    reOrderedRecipeProcessLines: AssemblyOrRecipeProcessLineArray
  ) {
    if (!reOrderedRecipeProcessLines.length) {
      throw new BrewManError(messages.recipe_process_group_no_lines_to_reorder);
    }

    const productionProcessGroupId = getProductionProcessGroupId(
      reOrderedRecipeProcessLines[0]
    );

    const notAllRecipeGroupIdsMatch = reOrderedRecipeProcessLines.some(
      (line) =>
        isAssemblyProcessLine(line)
          ? !line.readOnly.assemblyProcessGroupId.equals(
              productionProcessGroupId
            )
          : !line.readOnly.recipeProcessGroupId.equals(productionProcessGroupId)
    );

    if (notAllRecipeGroupIdsMatch) {
      throw new BrewManError(
        messages.recipe_process_group_not_all_lines_are_part_of_the_same_group
      );
    }

    const recipeProcessGroup = getProductionGroupById(
      internalRecipeProcessGroups.value,
      productionProcessGroupId
    );

    if (
      recipeProcessGroup.lines.length !== reOrderedRecipeProcessLines.length
    ) {
      throw new BrewManError(
        messages.recipe_process_group_re_order_lines_does_not_match_existing_lines
      );
    }

    const looselyTypedProcessGroups =
      recipeProcessGroup.lines as AssemblyOrRecipeProcessLineArray;

    looselyTypedProcessGroups.splice(
      0,
      recipeProcessGroup.lines.length,
      ...reOrderedRecipeProcessLines
    );
  }

  function removeRecipeProcessLineFromGroup(
    productionProcessGroupId: Guid,
    productionProcessLineId: Guid
  ) {
    const recipeProcessGroup = getProductionGroupById(
      internalRecipeProcessGroups.value,
      productionProcessGroupId
    );

    const existingRecipeProcessGroupLineIndex =
      recipeProcessGroup.lines.findIndex((line) =>
        line.id.equals(productionProcessLineId)
      );

    if (existingRecipeProcessGroupLineIndex === -1) {
      throw new BrewManError(
        translate(keys.recipe_process_line_unable_to_find, {
          id: productionProcessLineId,
        }).toString()
      );
    }

    recipeProcessGroup.lines.splice(existingRecipeProcessGroupLineIndex, 1);
  }

  function updateRecipeProcessLineSequenceInGroup(
    productionProcessGroupId: Guid,
    productionProcessLineId: Guid,
    delta: -1 | 1
  ) {
    const recipeProcessGroup = getProductionGroupById(
      internalRecipeProcessGroups.value,
      productionProcessGroupId
    );

    const existingRecipeProcessGroupLineIndex =
      recipeProcessGroup.lines.findIndex((line) =>
        line.id.equals(productionProcessLineId)
      );

    if (existingRecipeProcessGroupLineIndex === -1) {
      throw new BrewManError(
        translate(keys.recipe_process_line_unable_to_find, {
          id: productionProcessLineId,
        }).toString()
      );
    }

    const newTargetIndex = existingRecipeProcessGroupLineIndex + delta;

    if (
      newTargetIndex > recipeProcessGroup.lines.length - 1 ||
      newTargetIndex < 0
    ) {
      throw new BrewManError(messages.recipe_process_group_out_of_bounds);
    }

    const [lineToReOrder] = recipeProcessGroup.lines.splice(
      existingRecipeProcessGroupLineIndex,
      1
    );

    // Casting to a looser type won't cause function signatures to prevent
    // actions we know are correct, but hard to infer
    const looselyTypedProcessGroupLines =
      recipeProcessGroup.lines as AssemblyOrRecipeProcessLineArray;

    looselyTypedProcessGroupLines.splice(newTargetIndex, 0, lineToReOrder);
    recipeProcessGroup.lines.forEach((line, idx) => (line.sequence = idx)); // Remap idx
  }

  function removeVesselRequirementFromGroup(
    productionProcessGroupId: Guid,
    vesselRequirementIdToRemove: Guid
  ) {
    const recipeProcessGroup = getProductionGroupById(
      internalRecipeProcessGroups.value,
      productionProcessGroupId
    );

    const vesselRequirementIndex =
      recipeProcessGroup.vesselRequirements.findIndex((vesselRequirement) =>
        vesselRequirement.id.equals(vesselRequirementIdToRemove)
      );

    if (vesselRequirementIndex === -1) {
      throw new BrewManError(
        translate(keys.recipe_process_cannot_find_vessel_requirement, {
          id: vesselRequirementIdToRemove,
        }).toString()
      );
    }

    recipeProcessGroup.vesselRequirements.splice(vesselRequirementIndex, 1);
    recipeProcessGroup.vesselRequirements.splice(vesselRequirementIndex, 1);
  }

  function addOrUpdateRecipeVesselRequirementInGroup(
    productionProcessGroupId: Guid,
    newOrUpdatedVesselRequirement: AssemblyOrRecipeVesselRequirement
  ) {
    const recipeProcessGroup = getProductionGroupById(
      internalRecipeProcessGroups.value,
      productionProcessGroupId
    );

    // If we're a new line, just add it
    if (newOrUpdatedVesselRequirement.id.isEmpty()) {
      newOrUpdatedVesselRequirement.id = Guid.create();
    }

    const newOrUpdatedVesselRequirementIndex =
      recipeProcessGroup.vesselRequirements.findIndex((vesselRequirement) =>
        vesselRequirement.id.equals(newOrUpdatedVesselRequirement.id)
      );

    // Casting to a looser type won't cause function signatures to prevent
    // actions we know are correct, but hard to infer
    const looselyTypedVesselRequirements =
      recipeProcessGroup.vesselRequirements as AssemblyOrRecipeProcessRequirementArray;

    if (newOrUpdatedVesselRequirementIndex === -1) {
      looselyTypedVesselRequirements.push(newOrUpdatedVesselRequirement);
    } else {
      looselyTypedVesselRequirements.splice(
        newOrUpdatedVesselRequirementIndex,
        1,
        newOrUpdatedVesselRequirement
      );
    }
  }

  function reOrderProductionProcessGroups(
    reOrderedProductionProcessGroups: AssemblyOrRecipeProcessGroupArray
  ) {
    if (!reOrderedProductionProcessGroups.length) {
      throw new BrewManError(messages.recipe_process_group_no_lines_to_reorder);
    }

    internalRecipeProcessGroups.value.splice(
      0,
      reOrderedProductionProcessGroups.length,
      ...reOrderedProductionProcessGroups
    );
  }

  function updateProductionProcessGroupSequence(
    productionProcessGroupId: Guid,
    delta: -1 | 1
  ) {
    const existingProductionProcessGroupIndex =
      internalRecipeProcessGroups.value.findIndex((group) =>
        group.id.equals(productionProcessGroupId)
      );

    if (existingProductionProcessGroupIndex === -1) {
      throw new BrewManError(
        translate(keys.recipe_process_group_unable_to_find, {
          id: productionProcessGroupId.toString(),
        }).toString()
      );
    }

    const newTargetIndex = existingProductionProcessGroupIndex + delta;

    if (
      newTargetIndex > internalRecipeProcessGroups.value.length - 1 ||
      newTargetIndex < 0
    ) {
      throw new BrewManError(messages.recipe_process_group_out_of_bounds);
    }

    const [lineToReOrder] = internalRecipeProcessGroups.value.splice(
      existingProductionProcessGroupIndex,
      1
    );

    internalRecipeProcessGroups.value.splice(newTargetIndex, 0, lineToReOrder);

    internalRecipeProcessGroups.value.forEach(
      (line, idx) => (line.readOnly.sequence = idx)
    );
  }

  function moveProcessLinesIntoGroup(
    processGroup: AssemblyOrRecipeProcessGroup,
    processLines: AssemblyOrRecipeProcessLineArray,
    startingIndex?: number
  ) {
    const allLinesHaveSameGroupAsDropTarget = processLines.every((line) =>
      getProductionProcessGroupId(line).equals(processGroup.id)
    );

    if (allLinesHaveSameGroupAsDropTarget) {
      return;
    }

    const groupToMoveTo = getProductionGroupById(
      internalRecipeProcessGroups.value,
      processGroup.id
    );

    processLines.forEach((line) => {
      const clonedLine = line.clone();

      if (isAssemblyProcessLine(clonedLine)) {
        clonedLine.readOnly.assemblyProcessGroupId = groupToMoveTo.id;
      } else {
        clonedLine.readOnly.recipeProcessGroupId = groupToMoveTo.id;
      }

      const looselyTypedProcessGroupLines =
        groupToMoveTo.lines as AssemblyOrRecipeProcessLineArray;

      looselyTypedProcessGroupLines.splice(
        startingIndex ?? looselyTypedProcessGroupLines.length,
        0,
        clonedLine
      );

      const originalGroup = getProductionGroupById(
        internalRecipeProcessGroups.value,
        getProductionProcessGroupId(line)
      );

      const indexInOriginalGroup = originalGroup.lines.findIndex(
        (originalLine) => line.id.equals(originalLine.id)
      );

      originalGroup.lines.splice(indexInOriginalGroup, 1);

      groupToMoveTo.lines.forEach((line, idx) => {
        line.sequence = idx;
      });
    });
  }

  return {
    internalRecipeProcessGroups,
    addOrUpdateRecipeProcessGroup,
    addOrUpdateRecipeProcessLineInGroup,
    removeRecipeProcessGroup,
    reOrderRecipeProcessGroupLines,
    removeRecipeProcessLineFromGroup,
    updateRecipeProcessLineSequenceInGroup,
    removeVesselRequirementFromGroup,
    addOrUpdateRecipeVesselRequirementInGroup,
    reOrderProductionProcessGroups,
    updateProductionProcessGroupSequence,
    moveProcessLinesIntoGroup,
  };
}
